// Merchandising badges (owner review 2026-07-13) — small filled pills on
// catalog rows ("Most Popular", "Start Here", …) that guide first-time
// visitors through an otherwise dense menu. Admin-editable per service
// (migration 067: `services.badge_label` + `services.badge_tone`).
//
// This module is the single source of truth for the tone palette. It is
// consumed by the SPA (Services grid, detail page, admin manager), the Astro
// catalog detail page, AND the server's zod validation — keep it free of
// React/DOM imports so all three can load it.

export const BADGE_TONES = ['gold', 'rose', 'evergreen'] as const;
export type BadgeTone = (typeof BADGE_TONES)[number];

/**
 * Per-tone presentation. `star` marks social-proof tones (popularity,
 * favourites); the directional `evergreen` ("Start Here") stays text-only —
 * the owner-approved mockup distinction.
 *
 * Class strings are full Tailwind literals (never composed) so the scanner
 * picks them up in both the Vite and Astro builds.
 */
export const BADGE_TONE_META: Record<BadgeTone, { label: string; classes: string; star: boolean }> =
  {
    gold: { label: 'Gold', classes: 'bg-amber-500 text-white', star: true },
    rose: { label: 'Rose', classes: 'bg-rose-500 text-white', star: true },
    evergreen: { label: 'Evergreen', classes: 'bg-emerald-700 text-white', star: false },
  };

/** Shared pill shell — sized for catalog rows; detail surfaces may append
 *  overrides. */
export const BADGE_PILL_CLASSES =
  'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[9px] font-semibold uppercase tracking-[0.18em] shadow-sm';

export function isBadgeTone(value: unknown): value is BadgeTone {
  return typeof value === 'string' && (BADGE_TONES as readonly string[]).includes(value);
}

/** Defensive resolve — an unknown/missing tone renders as gold rather than
 *  crashing a public page over a bad DB value. */
export function resolveBadgeTone(value: unknown): BadgeTone {
  return isBadgeTone(value) ? value : 'gold';
}
